// src/app/shell/top-nav/top-nav.component.ts
import { Component, EventEmitter, Output, inject } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { LucideAngularModule, Menu, LogOut } from 'lucide-angular';
import { NavigationService } from '../../core/navigation/navigation.service';
import { AuthService } from '../../core/auth/auth.service';
import { GlobalPaymentsLogoComponent } from '../../shared/global-payments-logo/global-payments-logo.component';

@Component({
  selector: 'app-top-nav',
  standalone: true,
  imports: [RouterModule, LucideAngularModule, GlobalPaymentsLogoComponent],
  template: `
    <nav class="flex items-center justify-between px-4 h-14 shrink-0"
         style="background: hsl(var(--topnav-background)); border-bottom: 1px solid hsl(var(--sidebar-border));">
      <!-- Left: hamburger (mobile only) + logo -->
      <div class="flex items-center gap-3">
        <button class="lg:hidden p-1" (click)="toggleSidebar.emit()"
                style="color: hsl(var(--topnav-foreground));">
          <lucide-icon [img]="MenuIcon" [size]="20" />
        </button>
        <app-global-payments-logo variant="light" size="sm" />
      </div>

      <!-- Center: section tabs (desktop only) -->
      <div class="hidden lg:flex items-center gap-1 overflow-x-auto">
        @for (section of nav.sections; track section.key) {
          <button (click)="router.navigate([section.basePath])"
                  class="px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                  [style.color]="nav.activeSection() === section.key
                    ? 'hsl(var(--topnav-active))'
                    : 'hsl(var(--topnav-foreground))'">
            {{ section.label }}
          </button>
        }
      </div>

      <!-- Right: avatar/logout button -->
      <button (click)="onLogout()" class="flex items-center gap-2 px-2 py-1 rounded-md"
              style="color: hsl(var(--topnav-foreground));">
        <lucide-icon [img]="LogOutIcon" [size]="18" />
        <span class="text-sm hidden sm:inline">Sign Out</span>
      </button>
    </nav>

    <!-- Mobile section tabs (visible only on mobile when sidebar is NOT shown) -->
    <div class="lg:hidden flex overflow-x-auto gap-1 px-2 py-1"
         style="background: hsl(var(--topnav-background)); border-bottom: 1px solid hsl(var(--sidebar-border));">
      @for (section of nav.sections; track section.key) {
        <button (click)="router.navigate([section.basePath])"
                class="flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] whitespace-nowrap rounded"
                [style.color]="nav.activeSection() === section.key
                  ? 'hsl(var(--topnav-active))'
                  : 'hsl(var(--topnav-foreground))'">
          <lucide-icon [img]="section.icon" [size]="16" />
          <span>{{ section.label }}</span>
        </button>
      }
    </div>
  `,
})
export class TopNavComponent {
  @Output() toggleSidebar = new EventEmitter<void>();

  readonly nav = inject(NavigationService);
  readonly authService = inject(AuthService);
  readonly router = inject(Router);

  readonly MenuIcon = Menu;
  readonly LogOutIcon = LogOut;

  onLogout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
